import { Outlet, NavLink, useNavigate, useLocation, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Home as HomeIcon, ClipboardList, PlusCircle, MessageCircle, User, LayoutDashboard, Zap, Briefcase, ArrowLeft } from "lucide-react";
import { Button } from "@/components/ui/button";

const CLIENT_TABS = [
  { to: "/home", icon: HomeIcon, label: "Home", testid: "nav-home" },
  { to: "/richieste", icon: ClipboardList, label: "Richieste", testid: "nav-requests" },
  { to: "/richieste/nuova", icon: PlusCircle, label: "Nuova", testid: "nav-new-request", accent: true },
  { to: "/chat", icon: MessageCircle, label: "Chat", testid: "nav-chat" },
  { to: "/profilo", icon: User, label: "Profilo", testid: "nav-profile" },
];

const PRO_TABS = [
  { to: "/pro", icon: LayoutDashboard, label: "Dashboard", testid: "nav-dashboard" },
  { to: "/pro/feed", icon: Zap, label: "Richieste", testid: "nav-feed" },
  { to: "/lavori", icon: Briefcase, label: "Lavori", testid: "nav-jobs" },
  { to: "/chat", icon: MessageCircle, label: "Chat", testid: "nav-chat" },
  { to: "/profilo", icon: User, label: "Profilo", testid: "nav-profile" },
];

const BACK_ROUTES = ["/richieste/", "/chat/", "/lavori/", "/pro/onboarding", "/richieste/nuova"];

export function AppLayout() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const tabs = user?.role === "professional" ? PRO_TABS : CLIENT_TABS;
  const desktopLinks = tabs.filter((t) => !t.accent);
  const showBack = BACK_ROUTES.some((r) => location.pathname.startsWith(r) && location.pathname !== r.replace(/\/$/, ""));

  return (
    <div className="min-h-screen bg-[#F8F9FA]">
      <div className="min-h-screen flex flex-col">
        <header className="sticky top-0 z-40 bg-white/80 backdrop-blur-xl border-b border-slate-100">
          <div className="mx-auto max-w-6xl flex items-center gap-2 h-14 sm:h-16 px-4 sm:px-6">
            {showBack && (
              <button data-testid="back-button" onClick={() => navigate(-1)} className="sm:hidden p-2 -ml-2 rounded-full hover:bg-slate-100 transition-colors active:scale-95" aria-label="Indietro">
                <ArrowLeft className="w-5 h-5 text-slate-700" />
              </button>
            )}
            <Link to="/" className="flex items-center gap-2 shrink-0" data-testid="logo-link">
              <img src="/icons/logo-mark.png" alt="WhooNow" className="w-8 h-8 rounded-lg object-contain bg-white" />
              <span className="font-heading font-bold text-lg tracking-tight text-slate-900" data-testid="brand-wordmark">
                Whoo<span className="text-whoo">Now</span>
              </span>
            </Link>
            <div className="flex-1" />
            {user && (
              <nav className="hidden sm:flex items-center gap-1" data-testid="desktop-nav">
                {desktopLinks.map(({ to, label, testid }) => (
                  <NavLink key={to} to={to} data-testid={`desktop-${testid}`}
                    className={({ isActive }) => `px-4 py-2 rounded-full text-sm font-semibold transition-colors ${isActive ? "bg-whoo-soft text-whoo" : "text-slate-600 hover:bg-slate-100"}`}>
                    {label}
                  </NavLink>
                ))}
              </nav>
            )}
            {user && user.role !== "professional" && (
              <Link to="/richieste/nuova" className="hidden sm:block">
                <Button data-testid="desktop-new-request-btn" className="rounded-full bg-whoo hover:bg-whoo-dark text-white font-semibold active:scale-95 transition-transform">
                  Richiedi un intervento
                </Button>
              </Link>
            )}
            {!user && (
              <div className="flex items-center gap-2">
                <Link to="/register?role=professional" className="hidden sm:block">
                  <Button data-testid="header-pro-btn" variant="ghost" className="rounded-full font-semibold text-slate-700 hover:bg-slate-100">
                    Diventa professionista
                  </Button>
                </Link>
                <Link to="/login">
                  <Button data-testid="header-login-btn" size="sm" className="rounded-full bg-whoo hover:bg-whoo-dark text-white font-semibold active:scale-95 transition-transform sm:h-10 sm:px-6">
                    Accedi
                  </Button>
                </Link>
              </div>
            )}
          </div>
        </header>

        <main className="flex-1 pb-24 sm:pb-10">
          <div className="mx-auto w-full max-w-md sm:max-w-3xl lg:max-w-6xl">
            <Outlet />
          </div>
        </main>

        {user && (
          <nav data-testid="bottom-nav" className="fixed bottom-0 inset-x-0 z-50 sm:hidden">
            <div className="mx-auto max-w-md bg-white/90 backdrop-blur-xl border-t border-slate-100 px-2 pb-[env(safe-area-inset-bottom)]">
              <div className="grid grid-cols-5">
                {tabs.map(({ to, icon: Icon, label, testid, accent }) => (
                  <NavLink
                    key={to}
                    to={to}
                    data-testid={testid}
                    className={({ isActive }) =>
                      `flex flex-col items-center gap-1 py-2.5 transition-colors active:scale-95 ${
                        accent
                          ? "text-whoo"
                          : isActive
                            ? "text-whoo"
                            : "text-slate-400 hover:text-slate-600"
                      }`
                    }
                  >
                    {accent ? (
                      <span className="bg-whoo text-white rounded-full p-2 -mt-5 shadow-lg shadow-orange-500/30 border-4 border-[#F8F9FA]">
                        <Icon className="w-6 h-6" />
                      </span>
                    ) : (
                      <Icon className="w-6 h-6" strokeWidth={2} />
                    )}
                    <span className={`text-[10px] font-semibold ${accent ? "-mt-0.5" : ""}`}>{label}</span>
                  </NavLink>
                ))}
              </div>
            </div>
          </nav>
        )}
      </div>
    </div>
  );
}
